/**
 * DuplicateLookupWidget — global GST / BIN duplicate search for every agent.
 *
 * Two modes:
 *  - GST (India)   → GET /api/leads/lookup/gst?q=…
 *  - BIN (Bangladesh) → GET /api/leads/lookup/bin?q=…
 *
 * Purpose: before an agent chases (or adds) a customer, they check the
 * identifier and instantly see who already owns it, avoiding duplicate
 * outreach and stepped-on conversations.
 */
import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Search, Mail, Phone, ArrowUpRight, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "../App";

const MODES = {
  gst: {
    endpoint: "/leads/lookup/gst",
    label: "GST (India)",
    placeholder: "Enter GST number or first 3+ characters…",
    idKey: "gst_number",
    idHeader: "GST",
    inputMode: "text",
    maxLen: 32,
    minLen: 3,
  },
  bin: {
    endpoint: "/leads/lookup/bin",
    label: "BIN (Bangladesh)",
    placeholder: "Enter BIN or first 3+ digits…",
    idKey: "bin_number",
    idHeader: "BIN",
    inputMode: "numeric",
    maxLen: 32,
    minLen: 3,
  },
  phone: {
    endpoint: "/leads/lookup/phone",
    label: "Phone",
    placeholder: "Enter phone (min 4 digits, any format)…",
    idKey: "phone",
    idHeader: "Phone",
    inputMode: "tel",
    maxLen: 20,
    minLen: 4,
  },
};

const DuplicateLookupWidget = () => {
  const [mode, setMode] = useState("gst");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const timerRef = useRef(null);
  const cfg = MODES[mode];

  const runLookup = useCallback(async (query, modeKey) => {
    const c = MODES[modeKey] || MODES.gst;
    setErr("");
    const trimmed = (query || "").trim();
    if (trimmed.length < c.minLen) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}${c.endpoint}`, { params: { q: trimmed } });
      setResults(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Lookup failed");
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runLookup(v, mode), 350);
  };

  const switchMode = (m) => {
    if (m === mode) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setMode(m);
    setResults(null);
    setErr("");
    if (q.trim().length >= (MODES[m]?.minLen || 3)) runLookup(q, m);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (timerRef.current) clearTimeout(timerRef.current);
    runLookup(q, mode);
  };

  return (
    <Card className="border-slate-200" data-testid="duplicate-lookup-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Search className="w-5 h-5 text-indigo-600" />
            Duplicate Lookup
            <span className="text-xs font-normal text-slate-400 ml-2">
              Check if this customer is already mapped to a teammate
            </span>
          </CardTitle>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="duplicate-lookup-mode-switch">
            {Object.entries(MODES).map(([k, m]) => (
              <button
                key={k}
                type="button"
                onClick={() => switchMode(k)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  mode === k
                    ? "bg-white text-indigo-700 shadow-sm border border-slate-200"
                    : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`duplicate-lookup-mode-${k}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex gap-2 items-start">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={q}
              onChange={onChange}
              placeholder={cfg.placeholder}
              className="pl-9 h-10 tabular-nums"
              inputMode={cfg.inputMode}
              maxLength={cfg.maxLen}
              data-testid="duplicate-lookup-input"
              autoComplete="off"
            />
          </div>
        </form>

        {loading && <p className="text-xs text-slate-400 mt-3">Searching…</p>}
        {err && !loading && (
          <p className="text-xs text-rose-600 mt-3 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {err}
          </p>
        )}
        {!loading && !err && results && (
          <div className="mt-3">
            {results.count === 0 ? (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                No lead found with this {cfg.idHeader} — you're clear to add it.
              </p>
            ) : (
              <>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">
                  Found {results.count} match{results.count === 1 ? "" : "es"} —
                  don't call these customers if they belong to another teammate.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="duplicate-lookup-results">
                    <thead className="bg-slate-50 border-y border-slate-200">
                      <tr>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-2 px-3">Company</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-2 px-3">{cfg.idHeader}</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-2 px-3">Owner</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-2 px-3">Contact</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {results.results.map((r) => (
                        <tr key={r.lead_id} data-testid={`duplicate-row-${r.lead_id}`}>
                          <td className="py-2 px-3 font-medium text-slate-900">
                            {r.company_name}
                            {r.region && (
                              <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                                {r.region}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 tabular-nums text-slate-700">{r[cfg.idKey] || "—"}</td>
                          <td className="py-2 px-3 text-slate-700">
                            {r.assigned_to_name || <span className="text-slate-400">Unassigned</span>}
                          </td>
                          <td className="py-2 px-3 text-xs text-slate-600">
                            {r.agent_email && (
                              <a
                                href={`mailto:${r.agent_email}`}
                                className="inline-flex items-center gap-1 hover:text-indigo-600"
                                data-testid={`duplicate-row-email-${r.lead_id}`}
                              >
                                <Mail className="w-3 h-3" />
                                {r.agent_email}
                              </a>
                            )}
                            {r.phone && (
                              <span className="ml-2 inline-flex items-center gap-1 text-slate-500">
                                <Phone className="w-3 h-3" /> {r.phone}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            <Link
                              to={`/leads/${r.lead_id}`}
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                              data-testid={`duplicate-row-open-${r.lead_id}`}
                            >
                              Open <ArrowUpRight className="w-3 h-3" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
        {!loading && !err && !results && (
          <p className="text-[11px] text-slate-400 mt-3">
            Type at least {cfg.minLen} {cfg.inputMode === "tel" || cfg.inputMode === "numeric" ? "digits" : "characters"} of the {cfg.idHeader} to search.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DuplicateLookupWidget;
